import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Check, RotateCcw, Loader2, Image as ImageIcon, FileText, Search, ChevronDown, ChevronLeft, ChevronRight, Link2, Unlink, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PartyCommand } from '@/components/inward/PartyCommand';
import { OpenPoBadge, needsPo } from '@/components/inward/PoPicker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as docApi from '@/services/documentationApi';
import { listWarehouses, getInwardWarehouseSummary } from '@/services/warehousesApi';

const ITEMS_PER_PAGE = 50;

// 2026-06 — Inward parcel classification. Mandatory selection before Confirm.
const PARCEL_TYPE_OPTIONS = [
  { value: 'goods', label: 'Goods / Material', hint: 'Sent to the section for full processing' },
  { value: 'document', label: 'Document', hint: 'Invoice / swatch / sample — receipt only, no accounts scan' },
  { value: 'trims', label: 'Trims', hint: 'Receipt only — accounts scan required' },
  { value: 'warehouse_transfer', label: 'Warehouse Transfer', hint: 'Warehouse-to-warehouse move' },
  { value: 'sample', label: 'Sample', hint: 'Receipt only — ends at bundle receipt, no accounts scan' },
  { value: 'job_worker_return', label: 'Job Worker Return', hint: 'Skips bundle receipt — goes to the section Job Worker Return screen' },
];

const InwardDocumentation = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [sections, setSections] = useState([]);
  const [vendorMaster, setVendorMaster] = useState([]);
  // 2026-05-24 — Sales Return support: Customer Master rendered next to
  // Vendor Master inside the same party-name dropdown.
  const [customerMaster, setCustomerMaster] = useState([]);
  const [selectedPartyType, setSelectedPartyType] = useState({}); // entryId -> 'vendor'|'customer'
  const [selectedCustomerCodes, setSelectedCustomerCodes] = useState({}); // entryId -> customer_code
  const [isLoading, setIsLoading] = useState(true);
  const [extractingIds, setExtractingIds] = useState(new Set());
  const [editingData, setEditingData] = useState({});
  const [selectedSections, setSelectedSections] = useState({});
  // 2026-06 — Per-entry parcel type selection (goods|document|trims|warehouse_transfer).
  const [selectedParcelTypes, setSelectedParcelTypes] = useState({});
  // 2026-06 — Global search bar (server-side across all entries).
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // 2026-06 — Warehouse filter + FY bifurcation (Phase 2).
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [warehouseSummary, setWarehouseSummary] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState({});
  const [noOpenPo, setNoOpenPo] = useState({}); // entryId -> true when the supplier has no open PO for the section
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState({});
  const [viewingImages, setViewingImages] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [editModalEntry, setEditModalEntry] = useState(null);
  const [editPartyOpen, setEditPartyOpen] = useState(false);
  const [moveToDraftEntry, setMoveToDraftEntry] = useState(null);
  const [isMovingToDraft, setIsMovingToDraft] = useState(false);
  
  // Internal Transfer linking state
  const [linkedTransfers, setLinkedTransfers] = useState({});  // entryId -> transfer_id
  const [pendingTransfers, setPendingTransfers] = useState({}); // sectionName -> transfers[]
  const [linkDialogEntry, setLinkDialogEntry] = useState(null);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);

  // Format number in Indian format (1,23,456.78)
  const formatIndianNumber = (value) => {
    if (!value) return '';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return value;
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  // Parse and format vendor doc date to DD/MM/YYYY
  const formatVendorDate = (dateStr) => {
    if (!dateStr) return '';
    
    // If already in DD/MM/YYYY format, return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Try to parse various date formats and convert to DD/MM/YYYY
    try {
      // Try parsing common formats: 26-Apr-2025, 08-01-2026, 2026-01-08, etc.
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        const day = String(parsed.getDate()).padStart(2, '0');
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const year = parsed.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      // If parsing fails, return original
    }
    
    return dateStr;
  };

  // Format date and time - timestamp is simple string "YYYY-MM-DD HH:MM:SS"
  const formatDateTime = (timestamp) => {
    if (!timestamp) return { date: '-', time: '-' };
    
    // Handle both formats: "YYYY-MM-DD HH:MM:SS" or ISO format
    let dateStr, timeStr;
    
    if (timestamp.includes('T')) {
      // ISO format - parse it
      const dt = new Date(timestamp);
      dateStr = dt.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      timeStr = dt.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } else {
      // Simple format "2026-01-10 12:00:00" - just parse and display
      const [datePart, timePart] = timestamp.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      dateStr = `${day}/${month}/${year}`;
      timeStr = `${hour}:${minute}`;
    }
    
    return { date: dateStr, time: timeStr };
  };

  // Uploader filter state — default to logged-in user's email so the list is
  // immediately scoped to their own uploads. "ALL" shows every uploader,
  // "UNTAGGED" shows historical entries that were uploaded before the
  // created_by tagging fix rolled out (i.e. `created_by == null`).
  // The dropdown is built dynamically from the `/uploaders` endpoint plus
  // the current logged-in user's email — no more hard-coded emails.
  const getLoggedInEmail = () => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return (u?.email || '').trim();
    } catch {
      return '';
    }
  };
  const [uploaderFilter, setUploaderFilter] = useState(getLoggedInEmail() || 'ALL');
  const [uploaderOptions, setUploaderOptions] = useState([]);
  // Free-text staff_id filter (the physical deliverer's ID stamped at the
  // gate, e.g. 'GEN015'). Used to correlate historical untagged entries.
  const [staffIdFilter, setStaffIdFilter] = useState('');
  const [staffIdInput, setStaffIdInput] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const skip = currentPage * ITEMS_PER_PAGE;
      let createdBy = null;
      if (uploaderFilter === 'UNTAGGED') createdBy = '__UNTAGGED__';
      else if (uploaderFilter !== 'ALL') createdBy = uploaderFilter;
      const [entriesData, sectionsData, vendorData, customerData, uploadersData] = await Promise.all([
        docApi.getDocumentationEntries(skip, ITEMS_PER_PAGE, showLegacy, createdBy, staffIdFilter || null, searchQuery || null, warehouseFilter || null),
        docApi.getSections(),
        apiClient.get('/admin/vendor-master').catch(() => ({ data: { vendors: [] } })),
        // 2026-06-08 — Customer Master fetched in parallel so the
        // inward-documentation party dropdown lists both vendors AND
        // customers (Sales Returns flow). `lite=true` returns the COMPLETE
        // customer_master list (no 2000-row cap) with only dropdown fields and
        // skips the slow per-customer credit enrichment.
        apiClient.get('/order-management/customer-master', { params: { lite: true } })
          .catch(() => ({ data: { customers: [] } })),
        docApi.listInwardUploaders().catch(() => ({ uploaders: [] })),
      ]);

      setEntries(entriesData.entries || []);
      setTotalEntries(entriesData.total || 0);
      setHasMore(entriesData.has_more || false);
      // 2026-06 — Seed the parcel-type selection from any already-saved value
      // so a re-visited / confirmed entry shows its stored classification.
      setSelectedParcelTypes(prev => {
        const next = { ...prev };
        (entriesData.entries || []).forEach(e => {
          const pt = e.documentation?.parcel_type;
          if (pt && next[e.entry_id] === undefined) next[e.entry_id] = pt;
        });
        return next;
      });
      setSections(sectionsData.sections || []);
      setVendorMaster((vendorData.data.vendors || []).map(v => typeof v === 'string' ? v : v.vendor_name).filter(Boolean));
      setCustomerMaster(
        (customerData.data.customers || [])
          .map(c => {
            // 2026-05-26 — Show "trade name - place" as the dropdown label
            // (user-requested). Falls back to legacy `customer_name` if either
            // trade_name or place is missing on the customer row (older
            // imports via /order-management/customer-master CSV only carry
            // {customer_code, customer_name, credit_limit}; the richer
            // /imports/customer-master CSV adds trade_name, place, gst, …).
            // The stored value (`vendor_name` field on the entry) keeps the
            // legacy `customer_name` so backend filtering and historical
            // reports continue to match.
            const trade = (c.trade_name || '').trim();
            const place = (c.place || '').trim();
            const legacyName = (c.customer_name || c.display_name || c.legal_name || '').trim();
            const label = trade
              ? (place ? `${trade} - ${place}` : trade)
              : (legacyName || place);
            return {
              name: legacyName || label,
              label,
              trade_name: trade,
              place,
              code: c.customer_code || '',
              // 2026-05-26 — Also carry `customer_id` from /imports-style
              // records so the backend can match by id even when the
              // legacy `customer_code` field is empty. Both ids travel
              // with the dropdown selection.
              customer_id: c.customer_id || '',
            };
          })
          .filter(c => c.name || c.label)
      );
      // Build dropdown list from uploaders endpoint + logged-in user's email.
      // No more hardcoded gate-inward emails — the source of truth is the
      // distinct `created_by` set in `inward_entries`.
      const loggedIn = getLoggedInEmail();
      const merged = Array.from(new Set([
        ...(loggedIn ? [loggedIn] : []),
        ...(uploadersData.uploaders || []),
      ].filter(Boolean))).sort();
      setUploaderOptions(merged);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load documentation entries');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, showLegacy, uploaderFilter, staffIdFilter, searchQuery, warehouseFilter]);

  // 2026-06 — Load warehouses + FY warehouse breakdown (Phase 2).
  useEffect(() => {
    (async () => {
      try {
        const [whData, sumData] = await Promise.all([
          listWarehouses(false).catch(() => ({ warehouses: [] })),
          getInwardWarehouseSummary().catch(() => ({ summary: [] })),
        ]);
        setWarehouses(whData.warehouses || []);
        setWarehouseSummary(sumData.summary || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const totalPages = Math.ceil(totalEntries / ITEMS_PER_PAGE);
  
  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (hasMore || currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Fuzzy match function for vendor name filtering
  const fuzzyMatch = (text, query) => {
    if (!query) return true;
    const textLower = (text || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const queryLower = query.toLowerCase().replace(/[^a-z0-9]/g, '');
    return textLower.includes(queryLower);
  };

  // Get filtered party list (vendors + customers) based on AI extracted value
  // 2026-05-24 — Returns { vendors: [name], customers: [{name, code}] } so
  // the dropdown can render both groups with headings.
  const getFilteredParties = (entryId) => {
    const aiExtractedValue = getFieldValue({ entry_id: entryId, documentation: entries.find(e => e.entry_id === entryId)?.documentation }, 'vendor_name');
    if (!aiExtractedValue || aiExtractedValue === '-') {
      return { vendors: vendorMaster, customers: customerMaster };
    }
    const v = vendorMaster.filter(name => fuzzyMatch(name, aiExtractedValue));
    // 2026-05-26 — Match against BOTH the legacy customer_name AND the new
    // "trade_name - place" label so AI-extracted vendor names like
    // "Mahalakshmi Stores - Madurai" hit even when the legacy customer_name
    // column carries only "Mahalakshmi Stores".
    const c = customerMaster.filter(cust =>
      fuzzyMatch(cust.name, aiExtractedValue) ||
      fuzzyMatch(cust.label, aiExtractedValue) ||
      fuzzyMatch(cust.trade_name, aiExtractedValue) ||
      fuzzyMatch(cust.place, aiExtractedValue)
    );
    // If neither matches, fall back to full lists so the operator can scroll
    if (v.length === 0 && c.length === 0) {
      return { vendors: vendorMaster, customers: customerMaster };
    }
    // AI matches first, then everyone else — a misread name must never hide a party.
    const vSet = new Set(v);
    const cSet = new Set(c);
    return {
      vendors: [...v, ...vendorMaster.filter(n => !vSet.has(n))],
      customers: [...c, ...customerMaster.filter(x => !cSet.has(x))],
    };
  };

  // Quick membership check across both masters (used by handleConfirm)
  const isInPartyMasters = (name) => {
    if (!name) return false;
    if (vendorMaster.includes(name)) return true;
    return customerMaster.some(c => c.name === name);
  };

  const autoExtract = async (entryId) => {
    setExtractingIds(prev => new Set(prev).add(entryId));
    try {
      const result = await docApi.extractDocumentation(entryId);
      
      // Update entry in state
      setEntries(prev => prev.map(e => 
        e.entry_id === entryId ? { ...e, documentation: result } : e
      ));
      
      toast.success(`Extracted data for ${result.document_barcode}`);
    } catch (error) {
      console.error('Error extracting:', error);
      toast.error(`Extraction failed for entry ${entryId}`);
    } finally {
      setExtractingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(entryId);
        return newSet;
      });
    }
  };

  const handleFieldChange = (entryId, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        [field]: value
      }
    }));
  };

  const handleSectionChange = (entryId, section) => {
    setSelectedSections(prev => ({
      ...prev,
      [entryId]: section
    }));
  };

  const handleVendorSelect = (entryId, vendor) => {
    setSelectedVendors(prev => ({
      ...prev,
      [entryId]: vendor
    }));
    // 2026-05-24 — Selecting from Vendor list clears any prior customer flag
    setSelectedPartyType(prev => ({ ...prev, [entryId]: 'vendor' }));
    setSelectedCustomerCodes(prev => {
      const { [entryId]: _drop, ...rest } = prev;
      return rest;
    });
    // Also update editingData with the selected vendor
    handleFieldChange(entryId, 'vendor_name', vendor);
    handleFieldChange(entryId, 'party_type', 'vendor');
    handleFieldChange(entryId, 'customer_code', '');
    // Close the popover
    setVendorPopoverOpen(prev => ({
      ...prev,
      [entryId]: false
    }));
  };

  // 2026-05-24 — Sales Return: select a customer for this inward entry.
  // The customer's name lives in `vendor_name` for back-compat, but we
  // tag `party_type='customer'` + carry `customer_code` for downstream.
  const handleCustomerSelect = (entryId, customer) => {
    setSelectedVendors(prev => ({ ...prev, [entryId]: customer.name }));
    setSelectedPartyType(prev => ({ ...prev, [entryId]: 'customer' }));
    setSelectedCustomerCodes(prev => ({ ...prev, [entryId]: customer.code }));
    handleFieldChange(entryId, 'vendor_name', customer.name);
    handleFieldChange(entryId, 'party_type', 'customer');
    handleFieldChange(entryId, 'customer_code', customer.code || '');
    // 2026-05-26 — Also forward `customer_id` so the backend can match
    // customers imported via /imports/customer-master (which carry
    // customer_id but no customer_code).
    handleFieldChange(entryId, 'customer_id', customer.customer_id || '');
    setVendorPopoverOpen(prev => ({ ...prev, [entryId]: false }));
  };

  const handleEditModal = (entry) => {
    setEditModalEntry(entry);
    if (!editingData[entry.entry_id]) {
      setEditingData(prev => ({
        ...prev,
        [entry.entry_id]: {
          vendor_name: entry.documentation?.vendor_name || '',
          vendor_doc_no: entry.documentation?.vendor_doc_no || '',
          vendor_doc_date: entry.documentation?.vendor_doc_date || '',
          qty: entry.documentation?.qty || '',
          value: entry.documentation?.value || '',
          vehicle_no: entry.documentation?.vehicle_no || ''
        }
      }));
    }
  };

  const handleSaveFromModal = () => {
    setEditModalEntry(null);
  };

  const handleViewImages = async (entryId, gcsUrls) => {
    try {
      const response = await apiClient.post(`/inward/entries/${entryId}/signed-urls`);
      setViewingImages(response.data.signed_urls || []);
    } catch (error) {
      console.error('Error fetching image URLs:', error);
      toast.error('Failed to load images');
    }
  };

  const handleConfirm = async (entry) => {
    // Validate all fields are filled
    const vendorName = selectedVendors[entry.entry_id] || getFieldValue(entry, 'vendor_name');
    const vendorDocNo = getFieldValue(entry, 'vendor_doc_no');
    const vendorDocDate = getFieldValue(entry, 'vendor_doc_date');
    const qty = getFieldValue(entry, 'qty');
    const value = getFieldValue(entry, 'value');
    const vehicleNo = getFieldValue(entry, 'vehicle_no');
    const section = selectedSections[entry.entry_id] || entry.documentation?.section;

    if (!vendorName || !vendorDocNo || !vendorDocDate || !qty || !value || !vehicleNo || !section) {
      toast.error('All fields are mandatory. Please fill in all details.');
      return;
    }

    // 2026-06 — Parcel type is mandatory before confirmation.
    const parcelType = selectedParcelTypes[entry.entry_id] || entry.documentation?.parcel_type;
    if (!parcelType) {
      toast.error('Please select a parcel type (Goods / Document / Trims / Warehouse Transfer / Sample / Job Worker Return) before confirming.');
      return;
    }

    // Check if vendor/customer is from the masters (must be selected from dropdown)
    if (!selectedVendors[entry.entry_id] && !isInPartyMasters(vendorName)) {
      toast.error('Party must be selected from the Vendor / Customer Master dropdown. The extracted name is not in either master list.');
      return;
    }

    try {
      // Prepare update data
      const updateData = { ...editingData[entry.entry_id] };
      // 2026-06 — always carry the chosen parcel type.
      updateData.parcel_type = parcelType;

      // Add selected vendor from dropdown if selected
      if (selectedVendors[entry.entry_id]) {
        updateData.vendor_name = selectedVendors[entry.entry_id];
      }
      // 2026-05-24 — Always carry the party flags so the backend can
      // validate against the correct master (Sales Returns vs Vendor).
      const partyType = selectedPartyType[entry.entry_id]
        || updateData.party_type
        || (customerMaster.some(c => c.name === vendorName) ? 'customer' : 'vendor');
      updateData.party_type = partyType;
      if (partyType === 'customer') {
        updateData.customer_code = selectedCustomerCodes[entry.entry_id]
          || updateData.customer_code
          || (customerMaster.find(c => c.name === vendorName)?.code || '');
        // 2026-05-26 — Forward `customer_id` too. /imports-style records
        // carry customer_id (not customer_code); the backend matcher
        // accepts either.
        updateData.customer_id = updateData.customer_id
          || (customerMaster.find(c => c.name === vendorName)?.customer_id || '');
      } else {
        updateData.customer_code = '';
        updateData.customer_id = '';
      }
      
      // Update any edited fields first
      if (Object.keys(updateData).length > 0) {
        await docApi.updateDocumentation(entry.entry_id, updateData);
      }
      
      // Update section
      if (selectedSections[entry.entry_id]) {
        await docApi.updateDocumentation(entry.entry_id, {
          section: selectedSections[entry.entry_id]
        });
      }
      
      // Confirm documentation (with optional IT link)
      const linkedTransfer = linkedTransfers[entry.entry_id];
      const linkedTransferId = linkedTransfer && !linkedTransfer._prelinked 
        ? linkedTransfer.transfer_id 
        : null;
      await docApi.confirmDocumentation(entry.entry_id, linkedTransferId);
      
      toast.success('Documentation confirmed successfully');
      fetchData();
    } catch (error) {
      console.error('Error confirming:', error);
      // Show the specific error message from the backend
      const errorMessage = error.response?.data?.detail || 'Failed to confirm documentation';
      toast.error(errorMessage);
    }
  };

  const handleMoveToDraft = async () => {
    if (!moveToDraftEntry) return;
    
    setIsMovingToDraft(true);
    try {
      await apiClient.put(`/inward/entries/${moveToDraftEntry.entry_id}/move-to-draft`);
      toast.success('Entry moved to drafts');
      fetchData();
    } catch (error) {
      console.error('Error moving to draft:', error);
      toast.error('Failed to move entry to draft');
    } finally {
      setIsMovingToDraft(false);
      setMoveToDraftEntry(null);
    }
  };

  const getFieldValue = (entry, field) => {
    if (editingData[entry.entry_id]?.[field] !== undefined) {
      return editingData[entry.entry_id][field];
    }
    return entry.documentation?.[field] || '';
  };

  // 2026-06 — Resolve the FULL party display (trade name + place) for a
  // selected vendor/customer so the trigger + modal show the place that
  // disambiguates same-named parties (was truncated to legacy name only).
  const resolvePartyLabel = (entryId) => {
    const name = selectedVendors[entryId] || getFieldValue({ entry_id: entryId, documentation: entries.find(e => e.entry_id === entryId)?.documentation }, 'vendor_name') || '';
    if (!name) return '';
    const code = selectedCustomerCodes[entryId] || editingData[entryId]?.customer_code;
    const cust = customerMaster.find(c => (code && c.code === code) || c.name === name || c.label === name);
    return cust ? (cust.label || cust.name) : name;
  };

  // Fetch pending internal transfers for a section
  const fetchPendingTransfers = useCallback(async (sectionName) => {
    if (!sectionName || pendingTransfers[sectionName]) return;
    try {
      const data = await docApi.getPendingInternalTransfers(sectionName);
      setPendingTransfers(prev => ({ ...prev, [sectionName]: data.transfers || [] }));
    } catch (error) {
      console.error('Error fetching pending transfers:', error);
    }
  }, [pendingTransfers]);

  // When section changes for an entry, fetch pending transfers
  const handleSectionChangeWithTransferFetch = (entryId, section) => {
    handleSectionChange(entryId, section);
    fetchPendingTransfers(section);
  };

  // Open the link dialog for an entry
  const openLinkDialog = (entry) => {
    const section = selectedSections[entry.entry_id] || entry.documentation?.section || entry.consignment_section;
    if (!section) {
      toast.error('Please select a section first');
      return;
    }
    fetchPendingTransfers(section);
    setLinkDialogEntry(entry);
    setLinkSearchQuery('');
  };

  // Admin role: enables retroactive INT-linking on already-confirmed entries
  const isAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'Admin'; }
    catch { return false; }
  })();

  // Link an IT to an entry. If the entry is ALREADY CONFIRMED, call the
  // retroactive admin endpoint immediately (bug fix 2026-05: documentation
  // user forgot to tag INT and the INT now shows as duplicate in section
  // queue). Otherwise stage the link for the standard confirm path.
  const handleLinkTransfer = async (entryId, transfer) => {
    const entry = entries.find(e => e.entry_id === entryId);
    const isConfirmed = entry?.documentation?.extraction_status === 'confirmed';
    if (isConfirmed) {
      try {
        const res = await apiClient.post(
          `/documentation/link-internal-transfer/${entryId}`,
          { linked_internal_transfer_id: transfer.transfer_id }
        );
        toast.success(
          res?.data?.message
          || `Linked. INT ${transfer.transfer_id} removed from section queue.`
        );
        setLinkDialogEntry(null);
        fetchData();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to link INT retroactively');
      }
      return;
    }
    setLinkedTransfers(prev => ({ ...prev, [entryId]: transfer }));
    setLinkDialogEntry(null);
    toast.success(`Linked ${transfer.transfer_id} to this entry`);
  };

  // Unlink an IT from an entry
  const handleUnlinkTransfer = (entryId) => {
    setLinkedTransfers(prev => {
      const copy = { ...prev };
      delete copy[entryId];
      return copy;
    });
    toast.info('Internal transfer unlinked');
  };

  // Initialize linked transfers from fetched entries
  useEffect(() => {
    entries.forEach(entry => {
      if (entry.linked_internal_transfer_id && !linkedTransfers[entry.entry_id]) {
        setLinkedTransfers(prev => ({
          ...prev,
          [entry.entry_id]: { transfer_id: entry.linked_internal_transfer_id, _prelinked: true }
        }));
      }
    });
  }, [entries]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Inward Entry Documentation</h1>
              <p className="text-sm text-[#E8DCC8]">AI-Powered Document Data Extraction</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="secondary" className="h-10">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-full mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-[#1A1F2E] font-display">
              {showLegacy ? 'Legacy Entries (Before 01-Apr-2026)' : 'Document Extraction'} ({totalEntries} entries)
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              {/* 2026-06 — Global search across all entries (server-side) */}
              <form
                onSubmit={(e) => { e.preventDefault(); setSearchQuery(searchInput.trim()); setCurrentPage(0); }}
                className="flex items-center gap-1"
                data-testid="global-search"
              >
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search doc no, vendor, barcode, vehicle..."
                    className="h-9 text-xs w-[260px] pl-8"
                    data-testid="global-search-input"
                  />
                </div>
                <Button type="submit" size="sm" variant="outline" className="h-9" data-testid="global-search-btn">
                  Search
                </Button>
                {searchQuery && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-9 text-xs"
                    onClick={() => { setSearchQuery(''); setSearchInput(''); setCurrentPage(0); }}
                    data-testid="global-search-clear"
                  >
                    Clear
                  </Button>
                )}
              </form>
              {/* 2026-06 — Warehouse filter (Phase 2) */}
              <Select
                value={warehouseFilter || 'all'}
                onValueChange={(v) => { setWarehouseFilter(v === 'all' ? '' : v); setCurrentPage(0); }}
              >
                <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="warehouse-filter-trigger">
                  <SelectValue placeholder="All warehouses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="warehouse-filter-all">All warehouses</SelectItem>
                  {warehouses.map((w) => (
                    <SelectItem key={w.warehouse_id} value={w.warehouse_id} data-testid={`warehouse-filter-${w.warehouse_id}`}>
                      {w.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__UNTAGGED__" data-testid="warehouse-filter-untagged">Untagged</SelectItem>
                </SelectContent>
              </Select>
              {/* Uploader (Logged-in user) filter */}
              <div className="flex items-center gap-2" data-testid="uploader-filter">
                <label className="text-xs text-gray-500">User:</label>
                <Select
                  value={uploaderFilter}
                  onValueChange={(v) => { setUploaderFilter(v); setCurrentPage(0); }}
                >
                  <SelectTrigger className="w-[220px] h-9 text-xs" data-testid="uploader-filter-trigger">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL" data-testid="uploader-filter-all">All users</SelectItem>
                    <SelectItem value="UNTAGGED" data-testid="uploader-filter-untagged">
                      Legacy / Untagged
                    </SelectItem>
                    {uploaderOptions.map((em) => (
                      <SelectItem key={em} value={em} data-testid={`uploader-filter-${em}`}>
                        {em}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Staff ID filter — physical deliverer's ID stamped at the gate
                  (e.g. GEN015). Useful for correlating historical untagged
                  entries since legacy rows lack `created_by`. */}
              <div className="flex items-center gap-2" data-testid="staff-id-filter">
                <label className="text-xs text-gray-500">Staff ID:</label>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setStaffIdFilter(staffIdInput.trim());
                    setCurrentPage(0);
                  }}
                  className="flex items-center gap-1"
                >
                  <Input
                    value={staffIdInput}
                    onChange={(e) => setStaffIdInput(e.target.value)}
                    placeholder="e.g. GEN015"
                    className="h-9 text-xs w-[140px]"
                    data-testid="staff-id-filter-input"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    variant="outline"
                    className="h-9"
                    data-testid="staff-id-filter-apply"
                  >
                    <Search className="w-3.5 h-3.5" />
                  </Button>
                  {staffIdFilter && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-9 text-xs"
                      onClick={() => {
                        setStaffIdFilter('');
                        setStaffIdInput('');
                        setCurrentPage(0);
                      }}
                      data-testid="staff-id-filter-clear"
                    >
                      Clear
                    </Button>
                  )}
                </form>
              </div>
              <button
                onClick={() => { setShowLegacy(!showLegacy); setCurrentPage(0); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showLegacy
                    ? 'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200'
                    : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                }`}
                data-testid="legacy-toggle-btn"
              >
                {showLegacy ? 'Show Current Entries' : 'Show Legacy Entries'}
              </button>
              <div className="text-sm text-gray-500">
                Showing {totalEntries > 0 ? currentPage * ITEMS_PER_PAGE + 1 : 0} - {Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalEntries)} of {totalEntries}
              </div>
            </div>
          </div>

          {/* 2026-06 — FY warehouse bifurcation strip (Phase 2). Current FY (on/after 01-Apr-2026) */}
          {!showLegacy && warehouseSummary.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-4 -mt-2" data-testid="warehouse-breakdown">
              <span className="text-xs text-gray-400 uppercase tracking-wide">FY 26-27 by warehouse:</span>
              {warehouseSummary.map((w) => {
                const active = warehouseFilter === w.warehouse_id;
                return (
                  <button
                    key={w.warehouse_id}
                    type="button"
                    onClick={() => { setWarehouseFilter(active ? '' : w.warehouse_id); setCurrentPage(0); }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      active ? 'bg-[#2D5F4F] text-white border-[#2D5F4F]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#2D5F4F]'
                    }`}
                    data-testid={`warehouse-chip-${w.warehouse_id}`}
                  >
                    {w.warehouse_name} <span className="opacity-70">({w.count})</span>
                  </button>
                );
              })}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F] mx-auto" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-[#5A5F6E]">
              No inward entries with images found
            </div>
          ) : (
            <div>
              <div className="space-y-3">
                {entries.map((entry, index) => {
                  const isExtracting = extractingIds.has(entry.entry_id);
                  const isConfirmed = entry.documentation?.extraction_status === 'confirmed';
                  const isDraft = entry.status === 'draft';
                  const isRejected = entry.consignment_status === 'rejected';
                  const canEdit = !isConfirmed || isRejected;
                  const { date, time } = formatDateTime(entry.timestamp);
                  const linked = linkedTransfers[entry.entry_id];
                  const currentSection = selectedSections[entry.entry_id] || entry.documentation?.section || entry.consignment_section;
                  // 2026-05-04: surface entries that synced without images so
                  // operators can manually fill data. AI Extract is disabled
                  // for these since there is nothing to OCR.
                  const imageCount = (entry.image_urls || []).length;
                  const hasImages = imageCount > 0;

                  return (
                    <div
                      key={entry.entry_id}
                      className={`border rounded-lg ${isRejected ? 'border-red-300 bg-red-50/50' : 'border-[#E5DFD0] bg-white'} ${isConfirmed && !isRejected ? 'opacity-75' : ''}`}
                      data-testid={`entry-card-${entry.entry_id}`}
                    >
                      {/* Row 1: Header — Barcode, meta, actions */}
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E5DFD0]/60">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-xs text-gray-400 font-medium w-6 shrink-0">{currentPage * ITEMS_PER_PAGE + index + 1}</span>
                          <span className="font-mono text-sm font-semibold text-[#1A1F2E] truncate">{entry.document_barcode}</span>
                          {isDraft && <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded shrink-0">Draft</span>}
                          {isRejected && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium shrink-0">Wrong Section</span>}
                          {isConfirmed && !isRejected && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium shrink-0">Confirmed</span>}
                          {!hasImages && (
                            <span
                              className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium shrink-0"
                              title="No document images were uploaded for this gate entry. Use Edit to fill data manually, or re-upload images via the Inward Form."
                              data-testid={`no-images-badge-${entry.entry_id}`}
                            >
                              No Images
                            </span>
                          )}
                          <Button
                            onClick={() => handleViewImages(entry.entry_id, entry.image_urls)}
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 shrink-0"
                            disabled={!hasImages}
                            title={hasImages ? 'View document images' : 'No images attached'}
                            data-testid={`view-images-btn-${entry.entry_id}`}
                          >
                            <ImageIcon className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#5A5F6E] shrink-0">
                          <span>{date} {time}</span>
                          <span className="bg-gray-100 px-1.5 py-0.5 rounded">{entry.staff_id}</span>
                          <span className="font-medium">{entry.bundle_count} bundles</span>
                        </div>
                        {isRejected && entry.rejected_by && (
                          <span className="text-xs text-red-500 ml-3 shrink-0">Rejected by: {entry.rejected_by}</span>
                        )}
                      </div>

                      {/* Row 1.5: Parcel Type — MANDATORY classification */}
                      <div className="px-4 py-2 border-b border-[#E5DFD0]/40 flex items-center gap-3 flex-wrap">
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide shrink-0">
                          Parcel Type <span className="text-red-500">*</span>
                        </div>
                        {canEdit ? (
                          <div className="flex items-center gap-1.5 flex-wrap" data-testid={`parcel-type-group-${entry.entry_id}`}>
                            {PARCEL_TYPE_OPTIONS.map((opt) => {
                              const active = (selectedParcelTypes[entry.entry_id] || entry.documentation?.parcel_type) === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  title={opt.hint}
                                  onClick={() => setSelectedParcelTypes(prev => ({ ...prev, [entry.entry_id]: opt.value }))}
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                    active
                                      ? 'bg-[#2D5F4F] text-white border-[#2D5F4F]'
                                      : 'bg-white text-gray-600 border-gray-300 hover:border-[#2D5F4F] hover:text-[#2D5F4F]'
                                  }`}
                                  data-testid={`parcel-type-${opt.value}-${entry.entry_id}`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                            {!(selectedParcelTypes[entry.entry_id] || entry.documentation?.parcel_type) && (
                              <span className="text-[10px] text-red-500 italic ml-1">Select one before confirming</span>
                            )}
                          </div>
                        ) : (
                          <span
                            className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium"
                            data-testid={`parcel-type-badge-${entry.entry_id}`}
                          >
                            {PARCEL_TYPE_OPTIONS.find(o => o.value === entry.documentation?.parcel_type)?.label || entry.documentation?.parcel_type || '—'}
                          </span>
                        )}
                      </div>

                      {/* Row 2: Vendor / Customer details — all inline */}
                      <div className="px-4 py-2 grid grid-cols-6 gap-3 items-center text-sm border-b border-[#E5DFD0]/40">
                        {/* Party (Vendor or Customer) */}
                        <div className="col-span-2">
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5 flex items-center gap-1.5">
                            <span>Vendor / Customer</span>
                            {(selectedPartyType[entry.entry_id] || entry.documentation?.party_type) === 'customer' && (
                              <span
                                className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider"
                                data-testid={`customer-badge-${entry.entry_id}`}
                              >
                                Sales Return
                              </span>
                            )}
                          </div>
                          {isExtracting ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2D5F4F]" />
                          ) : canEdit ? (
                            <Popover
                              open={vendorPopoverOpen[entry.entry_id]}
                              onOpenChange={(open) => setVendorPopoverOpen(prev => ({...prev, [entry.entry_id]: open}))}
                            >
                              <PopoverTrigger asChild>
                                <Button variant="outline" role="combobox" className="w-full h-7 justify-between text-xs font-normal truncate px-2" data-testid={`vendor-select-${entry.entry_id}`}>
                                  <span className="truncate">{selectedVendors[entry.entry_id] || getFieldValue(entry, 'vendor_name') || 'Select vendor / customer...'}</span>
                                  <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[320px] p-0" align="start">
                                <PartyCommand
                                  {...(vendorPopoverOpen[entry.entry_id] ? getFilteredParties(entry.entry_id) : { vendors: [], customers: [] })}
                                  onVendor={(vendor) => handleVendorSelect(entry.entry_id, vendor)}
                                  onCustomer={(cust) => handleCustomerSelect(entry.entry_id, cust)}
                                  ids={{
                                    search: `party-search-${entry.entry_id}`,
                                    vendor: `party-option-vendor-${entry.entry_id}`,
                                    customer: `party-option-customer-${entry.entry_id}`,
                                    label: 'party-option-customer-label',
                                    more: `party-more-${entry.entry_id}`,
                                  }}
                                />
                                {getFieldValue(entry, 'vendor_name') && (
                                  <div className="border-t p-2 bg-gray-50">
                                    <p className="text-xs text-gray-500">AI Extracted:</p>
                                    <p className="text-xs font-medium text-blue-600 truncate">{getFieldValue(entry, 'vendor_name')}</p>
                                  </div>
                                )}
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-[#1A1F2E] text-xs">{selectedVendors[entry.entry_id] || getFieldValue(entry, 'vendor_name') || '-'}</span>
                          )}
                        </div>
                        {/* Doc No */}
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Doc No</div>
                          <span className="text-xs text-[#1A1F2E]">{getFieldValue(entry, 'vendor_doc_no') || '-'}</span>
                        </div>
                        {/* Doc Date */}
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Doc Date</div>
                          <span className="text-xs text-[#1A1F2E]">{formatVendorDate(getFieldValue(entry, 'vendor_doc_date')) || '-'}</span>
                        </div>
                        {/* Qty + Value */}
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Qty / Value</div>
                          <span className="text-xs text-[#1A1F2E]">
                            {formatIndianNumber(getFieldValue(entry, 'qty')) || '-'}
                            {getFieldValue(entry, 'value') ? ` / ${formatIndianNumber(getFieldValue(entry, 'value'))}` : ''}
                          </span>
                        </div>
                        {/* Vehicle */}
                        <div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Vehicle</div>
                          <span className="text-xs text-[#1A1F2E]">{getFieldValue(entry, 'vehicle_no') || '-'}</span>
                        </div>
                      </div>

                      {/* Row 3: Section + IT Link + Actions */}
                      <div className="px-4 py-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {/* Section */}
                          {canEdit ? (
                            <Select
                              value={currentSection || ''}
                              onValueChange={(value) => handleSectionChangeWithTransferFetch(entry.entry_id, value)}
                            >
                              <SelectTrigger className={`h-7 text-xs w-40 ${isRejected ? 'border-red-400 bg-red-50' : ''}`} data-testid={`section-select-${entry.entry_id}`}>
                                <SelectValue placeholder="Select section..." />
                              </SelectTrigger>
                              <SelectContent>
                                {sections.map((section) => (
                                  <SelectItem key={section.section_id || section.section_name} value={section.section_name}>
                                    {section.section_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs text-[#1A1F2E] bg-gray-100 px-2 py-1 rounded">{entry.documentation?.section || '-'}</span>
                          )}

                          {/* Open PO check — supplier deliveries for a section that raises POs need an open PO of that supplier */}
                          {needsPo(selectedParcelTypes[entry.entry_id] || entry.documentation?.parcel_type,
                            selectedPartyType[entry.entry_id] || entry.documentation?.party_type) && (
                            <OpenPoBadge
                              entryId={entry.entry_id}
                              vendorName={selectedVendors[entry.entry_id] || getFieldValue(entry, 'vendor_name')}
                              section={currentSection}
                              onBlocked={(b) => setNoOpenPo((p) => (!!p[entry.entry_id] === b ? p : { ...p, [entry.entry_id]: b }))}
                            />
                          )}

                          {/* Internal Transfer Link */}
                          {linked ? (
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="text-xs bg-blue-50 border-blue-300 text-blue-700 font-mono" data-testid={`linked-it-${entry.entry_id}`}>
                                <Link2 className="w-3 h-3 mr-1" />
                                {linked.transfer_id}
                              </Badge>
                              {canEdit && !linked._prelinked && (
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600" onClick={() => handleUnlinkTransfer(entry.entry_id)} data-testid={`unlink-it-${entry.entry_id}`}>
                                  <Unlink className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ) : canEdit && currentSection ? (
                            <Button variant="outline" size="sm" className="h-7 text-xs border-dashed border-blue-400 text-blue-600 hover:bg-blue-50" onClick={() => openLinkDialog(entry)} data-testid={`link-it-btn-${entry.entry_id}`}>
                              <ArrowRightLeft className="w-3 h-3 mr-1" />
                              Link IT
                            </Button>
                          ) : isConfirmed && !isRejected && isAdmin && currentSection ? (
                            <Button
                              variant="outline" size="sm"
                              className="h-7 text-xs border-dashed border-amber-500 text-amber-700 hover:bg-amber-50"
                              onClick={() => openLinkDialog(entry)}
                              data-testid={`link-it-retro-btn-${entry.entry_id}`}
                              title="Retroactively link an Internal Transfer that was forgotten at confirmation time. Admin only."
                            >
                              <ArrowRightLeft className="w-3 h-3 mr-1" />
                              Link IT (Admin)
                            </Button>
                          ) : null}

                          {isRejected && <span className="text-xs text-red-500">Change section above</span>}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {canEdit && (
                            <>
                              {!isRejected && (
                                <Button
                                  onClick={() => autoExtract(entry.entry_id)}
                                  disabled={isExtracting || !hasImages}
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs border-blue-500 text-blue-500 hover:bg-blue-50 disabled:opacity-50"
                                  data-testid={`extract-${entry.entry_id}`}
                                  title={hasImages ? 'AI-extract data from images' : 'No images to extract from — use Edit to fill manually'}
                                >
                                  {isExtracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1" />AI Extract</>}
                                </Button>
                              )}
                              <Button onClick={() => handleEditModal(entry)} variant="outline" size="sm" className="h-7 text-xs" data-testid={`edit-${entry.entry_id}`}>Edit</Button>
                              {!isRejected && (
                                <Button onClick={() => setMoveToDraftEntry(entry)} variant="outline" size="sm" className="h-7 text-xs border-orange-500 text-orange-500 hover:bg-orange-50" data-testid={`move-draft-${entry.entry_id}`}>
                                  <FileText className="w-3.5 h-3.5 mr-1" />Draft
                                </Button>
                              )}
                              <Button
                                onClick={() => handleConfirm(entry)}
                                disabled={isExtracting || isDraft || !!noOpenPo[entry.entry_id]}
                                size="sm"
                                className={`h-7 text-xs text-white disabled:bg-gray-300 disabled:cursor-not-allowed ${isRejected ? 'bg-orange-500 hover:bg-orange-600' : 'bg-[#2D5F4F] hover:bg-[#234A3D]'}`}
                                data-testid={`confirm-${entry.entry_id}`}
                                title={isDraft ? "Entry must be submitted before confirmation" : noOpenPo[entry.entry_id] ? "No open Purchase Order for this supplier and section — ask Purchase to issue one" : (isRejected ? "Re-confirm with new section" : "Confirm documentation")}
                              >
                                <Check className="w-3.5 h-3.5 mr-1" />{isRejected ? 'Re-Confirm' : 'Confirm'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Pagination Controls */}
              {totalEntries > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5DFD0]">
                  <div className="text-sm text-gray-600">
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 0 || isLoading}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i;
                        } else if (currentPage < 3) {
                          pageNum = i;
                        } else if (currentPage > totalPages - 4) {
                          pageNum = totalPages - 5 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            disabled={isLoading}
                            className={`h-9 w-9 p-0 ${pageNum === currentPage ? 'bg-[#2D5F4F] text-white' : ''}`}
                          >
                            {pageNum + 1}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages - 1 || isLoading}
                      className="h-9"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  <div className="text-sm text-gray-600">
                    {totalEntries} total entries
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      <Dialog open={editModalEntry !== null} onOpenChange={() => setEditModalEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Extracted Data - {editModalEntry?.document_barcode}</DialogTitle>
            <DialogDescription>
              Review and edit the extracted document fields. Select the vendor or customer from the master list.
            </DialogDescription>
          </DialogHeader>
          {editModalEntry && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium">Vendor / Customer Name * (Select from Master)</Label>
                <Popover open={editPartyOpen} onOpenChange={setEditPartyOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full mt-1 min-h-12 h-auto py-2 justify-between text-sm font-normal"
                      data-testid="edit-modal-party-trigger"
                    >
                      <span className="text-left flex-1 whitespace-normal break-words">
                        {resolvePartyLabel(editModalEntry.entry_id) || 'Select vendor or customer from master...'}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[520px] p-0" align="start">
                    <PartyCommand
                      vendors={vendorMaster}
                      customers={customerMaster}
                      onVendor={(vendor) => { handleVendorSelect(editModalEntry.entry_id, vendor); setEditPartyOpen(false); }}
                      onCustomer={(cust) => { handleCustomerSelect(editModalEntry.entry_id, cust); setEditPartyOpen(false); }}
                      wrap
                      listClassName="max-h-[300px]"
                      emptyText="No vendor or customer found in master."
                      ids={{
                        search: 'edit-modal-party-search',
                        vendor: 'edit-modal-party-option-vendor',
                        customer: 'edit-modal-party-option-customer',
                        label: 'edit-modal-party-option-customer-label',
                        more: 'edit-modal-party-more',
                      }}
                    />
                  </PopoverContent>
                </Popover>
                {editingData[editModalEntry.entry_id]?.vendor_name && editingData[editModalEntry.entry_id]?.vendor_name !== selectedVendors[editModalEntry.entry_id] && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <p className="text-xs text-blue-600">AI Extracted: <span className="font-medium">{editingData[editModalEntry.entry_id]?.vendor_name}</span></p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Vendor Doc No *</Label>
                  <Textarea
                    value={editingData[editModalEntry.entry_id]?.vendor_doc_no || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'vendor_doc_no', e.target.value)}
                    className="mt-1 min-h-[80px]"
                    placeholder="Enter document number"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Vendor Doc Date * (DD/MM/YYYY)</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.vendor_doc_date || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'vendor_doc_date', e.target.value)}
                    className="mt-1 h-12"
                    placeholder="DD/MM/YYYY"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Qty (Quantity) *</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.qty || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'qty', e.target.value)}
                    onBlur={(e) => {
                      const formatted = formatIndianNumber(e.target.value);
                      handleFieldChange(editModalEntry.entry_id, 'qty', formatted);
                    }}
                    className="mt-1 h-12 text-base"
                    placeholder="Enter quantity"
                  />
                  <p className="text-xs text-[#8A8F9E] mt-1">Auto-formats in Indian style (1,00,000)</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Value (Amount) *</Label>
                  <Input
                    value={editingData[editModalEntry.entry_id]?.value || ''}
                    onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'value', e.target.value)}
                    onBlur={(e) => {
                      const formatted = formatIndianNumber(e.target.value);
                      handleFieldChange(editModalEntry.entry_id, 'value', formatted);
                    }}
                    className="mt-1 h-12 text-base"
                    placeholder="Enter value"
                  />
                  <p className="text-xs text-[#8A8F9E] mt-1">Auto-formats in Indian style (5,50,000.50)</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Vehicle No *</Label>
                <Input
                  value={editingData[editModalEntry.entry_id]?.vehicle_no || ''}
                  onChange={(e) => handleFieldChange(editModalEntry.entry_id, 'vehicle_no', e.target.value)}
                  className="mt-1 h-12"
                  placeholder="Enter vehicle number"
                />
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => setEditModalEntry(null)}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveFromModal}
                  className="flex-1 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Internal Transfer Dialog */}
      <Dialog open={linkDialogEntry !== null} onOpenChange={() => setLinkDialogEntry(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Link Internal Transfer
            </DialogTitle>
          </DialogHeader>
          {linkDialogEntry && (() => {
            const section = selectedSections[linkDialogEntry.entry_id] || linkDialogEntry.documentation?.section || linkDialogEntry.consignment_section;
            const transfers = pendingTransfers[section] || [];
            const filtered = linkSearchQuery
              ? transfers.filter(t =>
                  (t.transfer_id || '').toLowerCase().includes(linkSearchQuery.toLowerCase()) ||
                  (t.from_section || '').toLowerCase().includes(linkSearchQuery.toLowerCase()) ||
                  (t.document_number || '').toLowerCase().includes(linkSearchQuery.toLowerCase())
                )
              : transfers;

            return (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gate Entry:</span>
                    <span className="font-mono font-medium">{linkDialogEntry.document_barcode}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500">Section:</span>
                    <span className="font-medium">{section}</span>
                  </div>
                </div>

                <div>
                  <Input
                    placeholder="Search by IT number, from section..."
                    value={linkSearchQuery}
                    onChange={(e) => setLinkSearchQuery(e.target.value)}
                    className="h-9"
                    data-testid="link-it-search"
                  />
                </div>

                {filtered.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 text-sm">
                    {transfers.length === 0
                      ? `No pending internal transfers for ${section}`
                      : 'No matches found'}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[350px] overflow-y-auto">
                    {filtered.map(t => (
                      <div
                        key={t.transfer_id}
                        className="border rounded-lg p-3 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer transition-colors"
                        onClick={() => handleLinkTransfer(linkDialogEntry.entry_id, t)}
                        data-testid={`link-it-option-${t.transfer_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-semibold text-sm text-[#1A1F2E]">{t.transfer_id}</span>
                          <Badge variant="outline" className={`text-xs capitalize ${
                            t.status === 'shipped' ? 'bg-green-50 text-green-700 border-green-300' :
                            t.status === 'requested' ? 'bg-amber-50 text-amber-700 border-amber-300' :
                            'bg-gray-50 text-gray-600 border-gray-300'
                          }`}>
                            {t.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500">
                          <span>From: <span className="text-gray-700 font-medium">{t.from_section}</span></span>
                          <span>Bundles: <span className="text-gray-700 font-medium">{t.bundle_count}</span></span>
                          {t.staff_name && <span>By: {t.staff_name}</span>}
                        </div>
                        {t.created_at && (
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(t.created_at).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-xs text-gray-400 text-center pt-2 border-t">
                  Linking is optional. Click a transfer to link, or close to skip.
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialogs */}
      <Dialog open={viewingImages !== null} onOpenChange={() => setViewingImages(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Document Images - Click to enlarge</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto p-4">
            {viewingImages?.map((url, idx) => (
              <div
                key={idx}
                className="relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border-2 border-[#E5DFD0] hover:border-[#2D5F4F]"
                onClick={() => setEnlargedImage(url)}
                style={{ aspectRatio: '4/3' }}
              >
                <img
                  src={url}
                  alt={`Document ${idx + 1}`}
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={enlargedImage !== null} onOpenChange={() => setEnlargedImage(null)}>
        <DialogContent className="max-w-[98vw] max-h-[98vh] w-auto h-auto p-0 bg-black">
          <div className="relative flex items-center justify-center min-h-[80vh]">
            <img
              src={enlargedImage}
              alt="Enlarged document"
              className="max-w-full max-h-[95vh] object-contain"
              style={{ margin: 'auto' }}
            />
            <Button
              onClick={() => setEnlargedImage(null)}
              variant="ghost"
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-900"
            >
              ✕ Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move to Draft Confirmation */}
      <AlertDialog open={!!moveToDraftEntry} onOpenChange={() => setMoveToDraftEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to Draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move entry &quot;{moveToDraftEntry?.document_barcode}&quot; back to draft status. 
              The entry will need to be re-submitted by the inward entry user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMovingToDraft}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMoveToDraft}
              disabled={isMovingToDraft}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isMovingToDraft ? 'Moving...' : 'Move to Draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InwardDocumentation;